/**
 * Optional storybook helper — link a keepsake to a memory answer.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Gem, Link2, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { PricelessPossessionDialog } from '../archive/PricelessPossessionDialog';
import {
  fetchMemoryMarkersForTree,
  filterPossessions,
  possessionWhyLine,
} from '../../utils/memoryMarkers';
import { getDisplayName, isGenealogicalPerson } from '../../utils/companionProfiles';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { getApiBase } from '../../utils/apiBase';

const BACKEND_URL = getApiBase().replace(/\/api$/, '');

export function StorybookPossessionLink({
  treeId,
  targetPersonId = null,
  treePeople = [],
  linkedPossessionId = null,
  onLinkedPossessionChange,
}) {
  const [open, setOpen] = useState(false);
  const [possessions, setPossessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const genealogicalPeople = useMemo(
    () => (treePeople || []).filter((p) => isGenealogicalPerson(p)),
    [treePeople],
  );

  const load = useCallback(async () => {
    if (!treeId) {
      setPossessions([]);
      return;
    }
    setLoading(true);
    try {
      const rows = await fetchMemoryMarkersForTree(treeId, { possessionsOnly: true });
      setPossessions(filterPossessions(rows));
    } catch {
      setPossessions([]);
    } finally {
      setLoading(false);
    }
  }, [treeId]);

  useEffect(() => { load(); }, [load]);

  const linked = possessions.find((p) => p.id === linkedPossessionId) || null;

  const selectable = useMemo(() => {
    if (!targetPersonId) return possessions;
    return possessions.filter(
      (p) => p.primary_person_id === targetPersonId
        || p.attachment?.person_id === targetPersonId
        || (p.connected_person_ids || []).includes(targetPersonId),
    );
  }, [possessions, targetPersonId]);

  const handleSelect = (id) => {
    const next = linkedPossessionId === id ? null : id;
    onLinkedPossessionChange?.(next);
  };

  return (
    <>
      <div className="rounded-xl border border-dashed border-[#c9a961]/30 bg-[#c9a961]/5">
        <button
          type="button"
          className="flex w-full items-center justify-between px-4 py-2.5 text-left text-xs font-medium text-[#8b7355]"
          onClick={() => setOpen((o) => !o)}
          data-testid="storybook-possession-toggle"
        >
          <span className="flex items-center gap-1.5">
            <Gem className="h-3.5 w-3.5" strokeWidth={1.25} />
            Is there an object, photo or keepsake connected to this memory?
            {linked ? ' · linked' : ''}
          </span>
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {open ? (
          <div className="space-y-3 border-t border-[#c9a961]/20 px-4 pb-3 pt-2">
            <p className="text-[11px] leading-relaxed text-muted-foreground/80">
              Optional — link a treasured thing from your archive, or add one now. This does not interrupt your writing.
            </p>
            {linked ? (
              <div className="flex items-start gap-3 rounded-lg border border-[#c9a961]/25 bg-white/80 p-3">
                {linked.image_url ? (
                  <img
                    src={resolveAssetUrl(BACKEND_URL, linked.image_url)}
                    alt=""
                    className="h-14 w-14 shrink-0 rounded-lg object-cover"
                  />
                ) : (
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-[#c9a961]/10">
                    <Gem className="h-5 w-5 text-[#8b7355]/60" strokeWidth={1.25} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-sm text-[#1c1917]">{linked.title}</p>
                  <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{possessionWhyLine(linked)}</p>
                </div>
                <Button type="button" variant="ghost" size="sm" className="shrink-0 text-xs" onClick={() => onLinkedPossessionChange?.(null)}>
                  Unlink
                </Button>
              </div>
            ) : null}
            {!linked && loading ? (
              <p className="text-xs text-muted-foreground">Loading keepsakes…</p>
            ) : null}
            {!linked && !loading && selectable.length > 0 ? (
              <ul className="max-h-40 space-y-1 overflow-y-auto">
                {selectable.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-white/70"
                      onClick={() => handleSelect(p.id)}
                    >
                      <Link2 className="h-3.5 w-3.5 shrink-0 text-[#8b7355]/70" />
                      <span className="truncate font-serif">{p.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {!linked && !loading && selectable.length === 0 ? (
              <p className="text-xs text-muted-foreground/80">No treasured objects linked yet — add one below without leaving this page.</p>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-full gap-1.5 text-xs"
              onClick={() => setDialogOpen(true)}
            >
              <Plus className="h-3.5 w-3.5" /> Add a priceless possession
            </Button>
          </div>
        ) : null}
      </div>

      {treeId ? (
        <PricelessPossessionDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          treeId={treeId}
          defaultPersonId={targetPersonId}
          genealogicalPeople={genealogicalPeople}
          stories={[]}
          onSaved={async (saved) => {
            await load();
            if (saved?.id) onLinkedPossessionChange?.(saved.id);
          }}
        />
      ) : null}
    </>
  );
}

export default StorybookPossessionLink;
